import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Avatar, IconButton, Divider, Tooltip, Badge, Button } from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PeopleIcon from "@mui/icons-material/People";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import ReportIcon from "@mui/icons-material/Report";
import Navbar from "../components/navbar";
import { Link as RouterLink } from "react-router-dom";
import Cookies from "js-cookie";

const Dashboard = () => {
  const [monthlyData, setMonthlyData] = useState([]);
  const [reasonData, setReasonData] = useState([]);
  const [testCounts, setTestCounts] = useState({});
  const [jobStats, setJobStats] = useState({ pending: 0, accepted: 0, completed: 0 });
  const [clientsCount, setClientsCount] = useState(0);
  const [collectorsCount, setCollectorsCount] = useState(0);
  const [jobRequests, setJobRequests] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [reports, setReports] = useState([]);
  const tests = ["Alcohol","Amphetamines","Benzodiazepines","Buprenorphine","Blood","Other","Cocaine","Ketamine","Maritime Std","MDMA","Methadone","Methamphetamine","Morphine","Network Rail Std","Opiates","SSRI","TCA","THC"];

  useEffect(() => {
    // Fetch monthly test data, reasons, test counts
    const fetchScreen4Data = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/getscreen4data`);
        if (!response.ok) throw new Error("Failed to fetch client data");
        const data = await response.json();
        const clients = data.data || [];

        // Monthly chart
        const monthlyCounts = {};
        clients.forEach((client) => {
          const date = new Date(client.dateoftest);
          const month = date.toLocaleString("default", { month: "short" });
          monthlyCounts[month] = (monthlyCounts[month] || 0) + 1;
        });
        const monthlyDataArray = Object.keys(monthlyCounts).map((month) => ({ month, count: monthlyCounts[month] }));
        setMonthlyData(monthlyDataArray);

        // Reason chart
        const reasonCounts = {};
        clients.forEach((client) => {
          const reason = client.reasonForTest || "Unknown";
          reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
        });
        const reasonDataArray = Object.keys(reasonCounts).map((reason) => ({ reason, count: reasonCounts[reason] }));
        setReasonData(reasonDataArray);

        // Test counts
        const testCountsTemp = {};
        tests.forEach((test) => {
          testCountsTemp[test] = { screen: 0, confirm: 0 };
        });
        clients.forEach((client) => {
          tests.forEach((test) => {
            if (client[`${test}Screen`]) testCountsTemp[test].screen += 1;
            if (client[`${test}Confirm`]) testCountsTemp[test].confirm += 1;
          });
        });
        setTestCounts(testCountsTemp);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    // Fetch job requests stats
    const fetchJobStats = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/getjobrequests?status=all`);
        if (!response.ok) throw new Error("Failed to fetch job requests");
        const data = await response.json();
        let pending = 0, accepted = 0, completed = 0;
        (data.data || []).forEach((job) => {
          if (!job.isAccepted && !job.isCompleted) pending++;
          else if (job.isAccepted && !job.isCompleted) accepted++;
          else if (job.isAccepted && job.isCompleted) completed++;
        });
        setJobStats({ pending, accepted, completed });
        setJobRequests(Array.isArray(data.data) ? data.data.slice(0, 5) : []);
      } catch (error) {
        console.error("Error fetching job stats:", error);
      }
    };

    // Fetch clients count
    const fetchClients = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/getclients`);
        if (!response.ok) throw new Error("Failed to fetch clients");
        const data = await response.json();
        setClientsCount(Array.isArray(data) ? data.length : 0);
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };

    const fetchCollectors = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/getcollectors`);
        if (!response.ok) throw new Error("Failed to fetch collectors");
        const data = await response.json();
        setCollectorsCount(Array.isArray(data) ? data.length : 0);
      } catch (error) {
        console.error("Error fetching collectors:", error);
      }
    };

    // Fetch notifications
    const fetchNotifications = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/getallnotification`);
        if (!response.ok) throw new Error("Failed to fetch notifications");
        const data = await response.json();
        setNotifications(data.filter((n) => !n.status));
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    // Fetch reports (dummy, replace with actual API if available)
    const fetchReports = async () => {
      setReports([
        { name: "August Report", url: "/report" },
        { name: "July Report", url: "/report" },
      ]);
    };

    fetchScreen4Data();
    fetchJobStats();
    fetchClients();
    fetchCollectors();
    fetchNotifications();
    fetchReports();
  }, []);

  // Pie chart colors
  const pieColors = ["#6F4FA0", "#4381B8", "#80c209", "#19afe6", "#8fbfcf", "#f3ffdf", "#4d4b4b1f"];

  return (
    <Box sx={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f5f6fa 60%, #e3f0ff 100%)"
    }}>
      <Navbar />
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%", maxWidth: "1200px", mx: "auto" }}>
        <Typography variant="h4" fontWeight={700} mb={3} color="#4180B7" sx={{ letterSpacing: 1 }}>
          Admin Dashboard Overview
        </Typography>
        {/* Cards */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "center" }}>
          {[{
            label: "Total Tests",
            value: monthlyData.reduce((acc, cur) => acc + cur.count, 0),
            icon: <AssignmentIcon />, color: "#4180B7"
          }, {
            label: "Active Clients",
            value: clientsCount,
            icon: <PeopleIcon />, color: "#80c209"
          }, {
            label: "Collectors",
            value: collectorsCount,
            icon: <PeopleIcon />, color: "#7b61ff"
          }, {
            label: "Pending Jobs",
            value: jobStats.pending,
            icon: <PendingActionsIcon />, color: "#19afe6"
          }, {
            label: "Completed Jobs",
            value: jobStats.completed,
            icon: <CheckCircleIcon />, color: "#8fbfcf"
          }].map((card, idx) => (
            <Box key={idx} sx={{ flex: "1 1 220px", maxWidth: 260, minWidth: 220 }}>
              <Card
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 2,
                  boxShadow: 3,
                  borderRadius: 3,
                  transition: "transform 0.2s",
                  '&:hover': { transform: "scale(1.04)", boxShadow: 6 },
                  minHeight: 90,
                  background: "#fff"
                }}
              >
                <Avatar sx={{ bgcolor: card.color, mr: 2, width: 48, height: 48 }}>{card.icon}</Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>{card.label}</Typography>
                  <Typography variant="h5" fontWeight={700}>{card.value}</Typography>
                </Box>
              </Card>
            </Box>
          ))}
        </Box>

        {/* Charts Section */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mt: 1, justifyContent: "center" }}>
          <Box sx={{ flex: "1 1 520px", minWidth: 320, maxWidth: 620 }}>
            <Card sx={{ p: 3, boxShadow: 2, borderRadius: 3, minHeight: 350, height: "100%" }}>
              <Typography variant="h6" mb={2} color="#4180B7">Monthly Tests</Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthlyData}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <ReTooltip />
                  <Bar dataKey="count" fill="#80c209" radius={[10, 10, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Box>
          <Box sx={{ flex: "1 1 520px", minWidth: 320, maxWidth: 620 }}>
            <Card sx={{ p: 3, boxShadow: 2, borderRadius: 3, minHeight: 350, height: "100%" }}>
              <Typography variant="h6" mb={2} color="#4180B7">Reason for Tests</Typography>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={reasonData} dataKey="count" nameKey="reason" cx="50%" cy="50%" outerRadius={90} label>
                    {reasonData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Box>
        </Box>

        {/* Job Requests Status & Reports */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mt: 1, justifyContent: "center" }}>
          <Box sx={{ flex: "1 1 520px", minWidth: 320, maxWidth: 620 }}>
            <Card sx={{ p: 3, boxShadow: 2, borderRadius: 3, height: "100%" }}>
              <Typography variant="h6" mb={2} color="#4180B7">Job Requests Status</Typography>
              <Box sx={{ display: "flex", gap: 2, justifyContent: "space-between" }}>
                <Box>
                  <Typography variant="subtitle1">Pending</Typography>
                  <Typography variant="h5" color="#80c209">{jobStats.pending}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1">Accepted</Typography>
                  <Typography variant="h5" color="#19afe6">{jobStats.accepted}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1">Completed</Typography>
                  <Typography variant="h5" color="#8fbfcf">{jobStats.completed}</Typography>
                </Box>
              </Box>
            </Card>
          </Box>
          <Box sx={{ flex: "1 1 520px", minWidth: 320, maxWidth: 620 }}>
            <Card sx={{ p: 3, boxShadow: 2, borderRadius: 3, height: "100%" }}>
              <Typography variant="h6" mb={2} color="#4180B7">Reports</Typography>
              {reports.map((report, idx) => (
                <Box key={idx} display="flex" alignItems="center" mb={1}>
                  <ReportIcon sx={{ color: "#80c209", mr: 1 }} />
                  <Typography variant="body1">{report.name}</Typography>
                  <Tooltip title="Open Reports">
                    <IconButton component={RouterLink} to={report.url}>
                      <AssignmentIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Card>
          </Box>
        </Box>

        {/* Notifications Section */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mt: 1, justifyContent: "center" }}>
          {/* <Box sx={{ flex: "1 1 520px", minWidth: 320, maxWidth: 620 }}>
            <Card sx={{ p: 3, boxShadow: 2, borderRadius: 3, minHeight: 200 }}>
              <Typography variant="h6" mb={2} color="#4180B7">Recent Notifications</Typography>
              <Box sx={{ maxHeight: 180, overflowY: "auto" }}>
                {notifications.length === 0 ? (
                  <Typography>No new notifications.</Typography>
                ) : (
                  notifications.slice(0, 10).map((note, idx) => (
                    <Box key={idx} mb={1}>
                      <Typography variant="subtitle2" color="#f44336" fontWeight={600}>{note.title}</Typography>
                      <Typography variant="body2">{note.contant}</Typography>
                      <Divider sx={{ my: 1 }} />
                    </Box>
                  ))
                }
              </Box>
            </Card>
          </Box> */}
          <Box sx={{ flex: "1 1 520px", minWidth: 320, maxWidth: 620 }}>
            {/* <Card sx={{ p: 3, boxShadow: 2, borderRadius: 3, minHeight: 200, paddingTop: 6 }}>
              <Typography variant="h6" mb={2} color="#4180B7">Recent Job Requests</Typography>
              <Box>
                {jobRequests.length === 0 ? (
                  <Typography>No recent job requests.</Typography>
                ) : (
                  jobRequests.map((job) => (
                    <Box key={job._id} mb={1} display="flex" justifyContent="space-between">
                      <Typography variant="body2" fontWeight={600}>{job.jobReferenceNo}</Typography>
                      <Typography variant="body2" color="#666">{job.customer}</Typography>
                    </Box>
                  ))
                }
              </Box>
            </Card>  */}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
